import { useEffect, useRef, useState } from 'react';
import { toastText } from 'utils/utils';

interface SpeechInputOptions {
	// Fired as the transcript grows, including interim (not-yet-final) words,
	// so the composer fills in live while the user is still talking.
	onTranscript: (text: string) => void;
	// Fired once when the session ends, with the finished transcript. Separate
	// from onTranscript so the caller can do end-of-dictation work — focusing
	// its input, say — without doing it on every interim result.
	onComplete?: (text: string) => void;
}

/**
 * Browser speech-to-text for a chat composer, shared by the floating panel and
 * the chat page so both dictate the same way.
 *
 * Deliberately does NOT send the message: voice input is a dictation aid, not
 * a submit trigger, so the transcript lands in the composer and the user still
 * reviews and edits before it goes out.
 */
const useSpeechInput = ({ onTranscript, onComplete }: SpeechInputOptions) => {
	const [isListening, setIsListening] = useState(false);
	const recognitionRef = useRef<any>(null);

	// The callbacks are re-created on every render by most callers, so they are
	// read through a ref — otherwise the handlers below would close over the
	// values from the render that started the session and write stale text.
	const handlersRef = useRef({ onTranscript, onComplete });
	handlersRef.current = { onTranscript, onComplete };

	// An open mic must not outlive the component that opened it.
	useEffect(() => {
		return () => {
			recognitionRef.current?.stop?.();
		};
	}, []);

	const toggleListening = () => {
		if (isListening) {
			recognitionRef.current?.stop();
			return;
		}

		const SpeechRecognitionCtor =
			(window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

		if (!SpeechRecognitionCtor) {
			toastText('Voice input is not supported in this browser.', 'error');
			return;
		}

		const recognition = new SpeechRecognitionCtor();
		recognition.lang = 'en-US';
		recognition.interimResults = true;
		// `continuous: false` treated any brief pause (thinking, taking a
		// breath, speaking slowly) as the end of speech and closed the mic
		// almost immediately. `continuous: true` keeps the session open
		// across those natural pauses — it now only stops when the user
		// clicks the mic button again, or the browser's own much longer
		// no-speech-at-all timeout gives up.
		recognition.continuous = true;
		recognition.maxAlternatives = 1;

		// Accumulates each finalized chunk of speech across the whole
		// session (continuous mode finalizes in bursts, not all at once, as
		// the user pauses between sentences) — kept in a closure variable
		// rather than state since it needs to be read synchronously inside
		// onend, without waiting on a state update.
		let finalTranscript = '';
		let lastInterimTranscript = '';

		recognition.onstart = () => setIsListening(true);
		recognition.onerror = (event: any) => {
			// The browser's own "gave up waiting for any speech at all"
			// timeout — distinct from a mic-access failure, and expected to
			// fire sometimes now that the session stays open much longer.
			// Let onend below send whatever was actually captured.
			if (event?.error === 'no-speech') {
				recognition.stop();
				return;
			}
			setIsListening(false);
			toastText('Could not access the microphone.', 'error');
		};
		recognition.onend = () => {
			setIsListening(false);
			const transcript = (finalTranscript || lastInterimTranscript).trim();
			if (transcript) {
				handlersRef.current.onComplete?.(transcript);
			}
		};
		recognition.onresult = (event: any) => {
			let interimTranscript = '';
			for (let i = event.resultIndex; i < event.results.length; i++) {
				const result = event.results[i];
				if (result.isFinal) {
					finalTranscript += result[0].transcript;
				} else {
					interimTranscript += result[0].transcript;
				}
			}
			lastInterimTranscript = interimTranscript;
			handlersRef.current.onTranscript(finalTranscript + interimTranscript);
		};

		recognitionRef.current = recognition;
		recognition.start();
	};

	return { isListening, toggleListening };
};

export default useSpeechInput;
